import React from 'react';
import type {
  ComponentProps,
  TranscriptSpeakerElement,
} from '../../../types/aha-editor';
import { useSlateStatic } from 'slate-react';
import { Node } from 'slate';
import EditorState, {
  Namespace,
  TranscriptsKey,
} from '../../../util/EditorState';
import { isTranscriptCueElement } from '../../../types/guards';
import styles from './Transcript.module.css';
import useAhaEditor from '../../../components/useAhaEditor';
import { secondsToTime } from '../utils/Time';
import { ApplicationView } from '@aha-app/mvc';

interface TranscriptSpeakerComponentProps
  extends ComponentProps<TranscriptSpeakerElement> {}

const speakerColors = [
  'var(--aha-blue-500)',
  'var(--aha-gold-400)',
  'var(--aha-amethyst-200)',
  'var(--aha-jade-400)',
  'var(--aha-tourmaline-400)',
  'var(--aha-garnet-400)',
  'var(--aha-lapis-500)',
  'var(--aha-peridot-500)',
  'var(--aha-amethyst-300)',
  'var(--aha-gold-500)',
  'var(--aha-tourmaline-300)',
  'var(--aha-garnet-300)',
  'var(--aha-blue-400)',
  'var(--aha-citrine-500)',
  'var(--aha-lapis-300)',
  'var(--aha-jade-500)',
  'var(--aha-rhodonite-400)',
  'var(--aha-amethyst-400)',
  'var(--aha-topaz-500)',
  'var(--aha-emerald-500)',
];

interface SpeakerInfo {
  speakerNumber: number;
}
type SpeakerMap = Record<string, SpeakerInfo>;

const TranscriptSpeakerComponent: React.FC<
  React.PropsWithChildren<TranscriptSpeakerComponentProps>
> = ({ attributes, element, children }) => {
  const editor = useSlateStatic();

  // Find range of cues of our children.
  let startTime: number | undefined;
  let endTime: number | undefined;
  const cueNodes = Node.descendants(element);
  for (const cueEntry of cueNodes) {
    const [cue] = cueEntry;
    if (!isTranscriptCueElement(cue)) continue;

    if (!startTime || cue.cue_startTime < startTime) {
      startTime = cue.cue_startTime;
    }
    if (!endTime || cue.cue_endTime > endTime) {
      endTime = cue.cue_endTime;
    }
  }

  // Color the speaker.
  let speakerNumber: number = 1;
  if (element.speaker_name && element.speaker_name.length > 0) {
    let speakerMap: SpeakerMap = EditorState.get(
      editor,
      Namespace.History,
      TranscriptsKey.SpeakerInfo
    );
    if (!speakerMap) {
      speakerMap = {};
      EditorState.set(
        editor,
        Namespace.History,
        TranscriptsKey.SpeakerInfo,
        speakerMap
      );
    }
    if (speakerMap[element.speaker_name]) {
      speakerNumber = speakerMap[element.speaker_name].speakerNumber;
    } else {
      speakerNumber = Object.keys(speakerMap).length + 1;
      speakerMap[element.speaker_name] = {
        speakerNumber: speakerNumber,
      };
    }
  }

  let speaker;

  const ahaEditor = useAhaEditor();
  const transcriptProvider = ahaEditor.getProvider('transcript');
  if (transcriptProvider) {
    const transcriptSpeakerName = element.speaker_name;

    // Thanks to ApplicationView, whenever the interview will change (used in getDisplaySpeakerName), this component will be re-rendered
    const displaySpeakerName = transcriptProvider.getDisplaySpeakerName(
      element.speaker_name
    );

    const speakerPath = transcriptProvider.getSpeakerPath(
      transcriptSpeakerName
    );
    if (speakerPath) {
      speaker = (
        <a href={speakerPath} data-drawer-url={speakerPath}>
          <b>{displaySpeakerName}</b>
        </a>
      );
    }

    if (!speaker) {
      speaker = <b>{displaySpeakerName}</b>;
    }
  }

  const onTimeClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    if (transcriptProvider && startTime) {
      transcriptProvider.seekTo(startTime);
    }
  };

  let timing: React.ReactNode | undefined;
  if (startTime && endTime) {
    timing = (
      <span onClick={onTimeClick} className={styles.timestamp}>
        {secondsToTime(startTime)}-{secondsToTime(endTime)}
      </span>
    );
  }

  const speakerInfo = speaker ? (
    <>
      {speaker} {timing}
    </>
  ) : (
    <b>&nbsp;</b> // Provider is registered after ahaEditor has rendered once
  );

  return (
    <div
      {...attributes}
      className={styles.speaker}
      style={{
        borderLeftColor: speakerColors[speakerNumber % speakerColors.length],
      }}
    >
      <span contentEditable={false} className={styles.speakerInfo}>
        {speakerInfo}
      </span>
      <span>{children}</span>
    </div>
  );
};

export default ApplicationView(TranscriptSpeakerComponent);
